import { Icon } from "@iconify/react";
import { Button, Card, DatePicker } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import * as XLSX from "xlsx";
import { magpBEApi } from "~/Services";
import { LoginData } from "./Dashboard";

const { RangePicker } = DatePicker;

interface ChartCardProps {
  data: Array<{
    label: string;
    additionalLabel: string;
    value: number;
  }>;
  startDateType: any;
  endDateType: any;
  setStartDateType: any;
  setEndDateType: any;
  dataType: any;
  setDataType: any;
  title: any;
  exportData: any;
}
//  ({ active, payload, label }: TooltipProps<number, string>)
const CustomTooltip = ({
  active,
  payload,
  label,
  coordinate,
  viewBox,
  dataType,
}: TooltipProps<number, string> & {
  coordinate?: { x: number; y: number };
  viewBox?: { width: number };
  dataType?: string;
}) => {
  if (active && payload && payload.length) {
    const { value, payload: dataPayload } = payload[0];
    const showAdditionalLabel = label !== dataPayload.additionalLabel;
    const formattedLabel = dataType === "daily" ? moment(label).format("DD-MM-YYYY") : label;

    const xOffset = coordinate?.x || 0;
    const chartWidth = viewBox?.width || 0;
    const tooltipWidth = 200;

    let leftPosition = xOffset;
    let transform = "translateX(-50%)";

    if (xOffset + tooltipWidth / 2 > chartWidth) {
      leftPosition = xOffset - tooltipWidth;
      transform = "translateX(0)";
    } else if (xOffset - tooltipWidth / 2 < 0) {
      leftPosition = xOffset;
      transform = "translateX(0)";
    }

    const tooltipStyle: React.CSSProperties = {
      position: "fixed",
      top: "105px",
      left: `${leftPosition}px`,
      transform,
      backgroundColor: "#712B90",
      opacity: 0.9,
      border: "2px solid #712B90",
      borderRadius: "8px",
      padding: "12px",
      color: "white",
      fontSize: "16px",
      pointerEvents: "none",
      zIndex: 1000,
      minWidth: `${tooltipWidth}px`,
    };

    return (
      <div className="bg-[#712B90] bg-opacity-80 border-2 border-[#712B90] rounded-lg p-4 text-white text-lg">
        <span style={{ fontWeight: "bold" }}>{formattedLabel}</span>
        <br />
        <span>Value: {value}</span>
        <br />
        {showAdditionalLabel && <span>{dataPayload.additionalLabel}</span>}
      </div>
    );
  }
  return null;
};

export const ChartCard: React.FC<ChartCardProps> = ({
  data,
  startDateType,
  endDateType,
  setStartDateType,
  setEndDateType,
  dataType,
  setDataType,
  title,
  exportData,
}) => {
  const [chartWidth, setChartWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const minimumPointWidth = 100;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const updateWidth = () => {
      const container = document.getElementById("chart-container");
      if (container) {
        setContainerWidth(container.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    if (containerWidth > 0) {
      const maxPointsInView = Math.floor(containerWidth / minimumPointWidth);
      if (data?.length <= maxPointsInView) {
        setChartWidth(containerWidth);
      } else {
        setChartWidth(data?.length * minimumPointWidth);
      }
    }
  }, [containerWidth, data?.length]);

  const handleDownload = async () => {
    setLoading(true);
    try {
      let data: LoginData[] = [];
      const response = await magpBEApi.get(exportData);
      if (response && (response.status === 200 || response.status === 202)) {
        console.log("response", response);
        if (response.data && response.data.data) {
          data = response.data.data;
        }
      } else {
        console.error("Failed to fetch data: Invalid status", response.status);
        return;
      }
      const formattedData = data.map((item, index) => ({
        "S.No": index + 1,
        Date: moment(item.date).format("DD-MM-YYYY"),
        "Login Type": item.loginType.charAt(0).toUpperCase() + item.loginType.slice(1),
        "Email Id": item.emailId,
        "Session Count": item.count === 0 ? "" : item.count,
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const columnWidths = [
        { wch: 5 }, // S.No
        { wch: 12 }, // Date
        { wch: 12 }, // Login Type
        { wch: 30 }, // Email Id
        { wch: 15 }, // Session Count
      ];
      worksheet["!cols"] = columnWidths;
      XLSX.utils.book_append_sheet(workbook, worksheet, "Login Details");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Login_Details_${moment().format("YYYY-MM-DD")}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading excel:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <Card className="w-full !rounded-2xl shadow-lg cursor-pointer">
        <div className="flex flex-col space-y-4">
          <div className="flex justify-between">
            <div className="flex items-center space-x-10">
              <RangePicker
                value={[
                  startDateType ? moment(startDateType, "YYYY-MM-DD") : null,
                  endDateType ? moment(endDateType, "YYYY-MM-DD") : null,
                ]}
                onChange={(dates) => {
                  const [start, end] = dates || [];
                  setStartDateType(start ? start.format("YYYY-MM-DD") : "");
                  setEndDateType(end ? end.format("YYYY-MM-DD") : "");
                }}
                format="DD-MM-YYYY"
                placeholder={["Select start date and end date", "Select start date and end date"]}
                className="mr-2 w-[400px]"
                disabledDate={(current) => current && current > moment().endOf("day")}
              />

              <Button.Group>
                <Button
                  type={dataType === "daily" ? "primary" : "default"}
                  onClick={() => setDataType("daily")}
                  className={`${
                    dataType === "daily" ? "!bg-[#712B90] !text-white" : "!bg-white !text-[#712B90]"
                  } !border-none w-[120px] !font-semibold !text-base`}
                >
                  Daily
                </Button>
                <Button
                  type={dataType === "weekly" ? "primary" : "default"}
                  onClick={() => setDataType("weekly")}
                  className={`${
                    dataType === "weekly" ? "!bg-[#712B90] !text-white" : "!bg-white !text-[#712B90]"
                  } !border-none w-[120px] !font-semibold !text-base`}
                >
                  Weekly
                </Button>
                <Button
                  type={dataType === "monthly" ? "primary" : "default"}
                  onClick={() => setDataType("monthly")}
                  className={`${
                    dataType === "monthly" ? "!bg-[#712B90] !text-white" : "!bg-white !text-[#712B90]"
                  } !border-none w-[120px] !font-semibold !text-base`}
                >
                  Monthly
                </Button>
              </Button.Group>
            </div>
            <div>
              <Button
                type="default"
                onClick={handleDownload}
                icon={
                  loading ? (
                    <Icon
                      icon="mdi:loading"
                      className="animate-spin mr-2"
                    />
                  ) : null
                }
                className="!border-[#712B90] !text-[#712B90]"
              >
                Download
              </Button>
            </div>
          </div>

          <div
            className="overflow-x-auto"
            id="chart-container"
          >
            <div style={{ width: `${chartWidth}px`, minWidth: "100%" }}>
              {chartWidth > 0 && (
                <ResponsiveContainer
                  width="100%"
                  height={350}
                >
                  <AreaChart
                    data={data}
                    margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorVisitors"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#8E44AD"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="#8E44AD"
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={true}
                      horizontal={true}
                      stroke="#E0E0E0"
                    />
                    <XAxis
                      dataKey="label"
                      axisLine={{ stroke: "#B0B0B0", strokeWidth: 2 }}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#666" }}
                      dy={10}
                      orientation="bottom"
                      tickFormatter={(label) => {
                        return dataType === "daily" ? moment(label).format("DD-MM-YYYY") : label;
                      }}
                    />
                    <YAxis
                      axisLine={{ stroke: "#B0B0B0", strokeWidth: 2 }}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#666" }}
                      dx={-10}
                      orientation="left"
                      domain={["auto", "auto"]}
                      allowDecimals={false}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickFormatter={() => ""}
                      axisLine={{ stroke: "#B0B0B0", strokeWidth: 2 }}
                      tickLine={false}
                      tick={false}
                    />
                    <XAxis
                      xAxisId="top"
                      orientation="top"
                      tickFormatter={() => ""}
                      axisLine={{ stroke: "#B0B0B0", strokeWidth: 2 }}
                      tickLine={false}
                      tick={false}
                    />
                    {/* <Tooltip content={<CustomTooltip />}/> */}
                    <Tooltip content={<CustomTooltip dataType={dataType} />} />
                    {/* <Tooltip
                      content={<CustomTooltip />}
                      // cursor={{ stroke: '#712B90', strokeWidth: 2 }}
                      position={{ x: 0, y: 0 }}
                      // isAnimationActive={false}
                      viewBox={{ width: chartWidth }}
                    /> */}
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#8E44AD"
                      strokeWidth={2}
                      fill="url(#colorVisitors)"
                      fillOpacity={1}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
